<template>
  <div>
    <button id="toggleBtn" data-toggle="modal" data-target="#topicThreadModal" hidden></button>
    <div :id="doc._id + '_content'" class="content mb-4" v-for="doc in feed" :key="doc._id">
      <div>
        <div class="content-header">
          <div class="d-flex align-items-start">
            <img
              v-if="doc.profile && doc.profile.upload && doc.profile.upload.path"
              class="content-header__avatar rounded-circle mt-1"
              width="40"
              height="40"
              :src="doc.profile.upload.path"
              alt
            />
            <img
              v-else
              class="content-header__avatar rounded-circle mt-1"
              width="40"
              height="40"
              src="../../../assets/avatar.png"
              alt
            />
            <div class="content-header__description ml-2">
              <div class="content-header__info">
                <router-link :to="'/profile/' + doc.profile_id" class="content-header__name">
                  {{ doc.profile.name }}
                  <ion-icon v-if="doc.is_admin" name="ios-compass"></ion-icon>
                </router-link>
                <div inline v-if="source !== 'circle'">
                  <span role="action" class="text-muted ml-1" v-if="doc.type === types[0]">posted in </span>
                  <span role="action" class="text-muted ml-1" v-else>created a new topic in </span>
                  <router-link
                    :to="'/circle/' + doc.circle_id"
                    class="content-header__supplementary"
                  >{{ doc.circle.title }} <ion-icon v-if="doc.circle.visible === false" name="lock"></ion-icon></router-link>
                </div>
                <div inline v-else>
                  <div inline v-if="doc.type === types[0]">
                    <span role="action" class="text-muted ml-1">&rsaquo;</span>
                    <a
                      href="#"
                      class="font-weight-bold ml-1"
                      @click.prevent="showThread(doc.topic_id)"
                    >{{ doc.topic.title }}</a>
                  </div>
                  <span role="action" class="text-muted ml-1" v-else>created a new topic</span>
                </div>
              </div>
              <div class="content-header__meta">
                <span role="role" v-if="doc.is_admin">Administrator</span>
                <span role="role" v-else>Member</span> &middot;
                <span role="time">{{ doc.created | happroxy }}</span>
              </div>
            </div>
            <div class="text-right flex-grow-1">
              <button
                inline
                class="btn btn-outline-secondary btn-sm text-capitalize"
                @click.prevent="openTopicEditor(doc)"
                v-if="doc.type === types[1]"
              >Edit {{doc.type}}</button>
            </div>
          </div>
        </div>

        <div class="content-body">
          <div class="content-body__title">
            <div inline v-if="source !== 'circle'">
              <a
                href="#"
                class="font-weight-bold"
                v-if="doc.type === types[0]"
                @click.prevent="showThread(doc.topic_id)"
              >{{ doc.topic.title }}</a>
              <a
                href="#"
                class="font-weight-bold"
                v-else
                @click.prevent="showThread(doc._id)"
              >{{ doc.title }}</a>
            </div>
            <div inline v-else-if="source === 'circle' && doc.type === types[1]">
              <a
                href="#"
                class="font-weight-bold"
                @click.prevent="showThread(doc._id)"
              >{{ doc.title }}</a>
            </div>
          </div>
          <div v-if="doc.type === types[0]" class="content-body__text">
            <p v-html="doc.body"></p>
          </div>
        </div>

        <div v-if="doc.type === types[0]" class="content-media px-3">
          <div
            class="card-columns photo-grid"
            :style="`column-count:${calculateColumns(doc.media_uploads)}`"
          >
            <div class="photo-grid__photo">
              <img
                v-for="img in doc.media_uploads"
                :key="img._id"
                class="img-fluid"
                v-lazy="img.upload.path"
                @click="showImage(img.upload.path, doc._id)"
              />
              <lightbox
                :id="'lightbox' + doc._id"
                ref="lightbox"
                :images="getImagesForLightBox(doc._id)"
              ></lightbox>
            </div>
          </div>
        </div>

        <div class="content-footer" v-if="doc.type === types[1] && canUserComment(doc.circle_id)">
          <div class="action-links d-flex justify-content-end"></div>

          <div class="add-comment d-flex align-items-start mt-4">
            <img class="rounded-circle mr-2" width="34" src="../../../assets/avatar.png" />
            <div class="form-group w-100">
              <div class="comment-input w-100">
                <textarea
                  :id="doc._id + '_feedinput'"
                  @input="checkValid($event, doc._id)"
                  type="text"
                  name="comment"
                  class="form-control"
                  placeholder="Add a comment"
                />
                <ion-icon name="ios-images" @click="toggleUpload(doc._id)"></ion-icon>
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <span class="form-error-text" hidden>A comment error message.</span>
                  <span></span>
                  <!-- Use v-else on this span for balance -->
                  <small :id="doc._id + '_validation'" class="text-light-grey">0/{{ MAX_CHAR_LEN }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Uploads -->
          <div
            :id="'uploads-group' + doc._id"
            class="form-group uploads-group"
            style="margin-left:38px"
            hidden
          >
            <div
              class="upload-frame mb-3"
              v-for="image in getDocImages(doc._id)"
              :key="image.id"
              :style="'background-image:' + image.src"
            >
              <span class="close" @click="removeImage(image.id)">
                <ion-icon name="ios-close"></ion-icon>
              </span>
            </div>

            <div
              class="upload-frame upload-frame--add"
              :class="[(getDocImages(doc._id).length === 0) ? 'mr-2' : '']"
              :hidden="getDocImages(doc._id).length === 4"
              @click="selectImageFromFS(doc._id)"
            >
              <span class="add">
                <ion-icon name="ios-add-circle"></ion-icon>
              </span>
              <input
                :id="doc._id + '_image-input'"
                type="file"
                accept=".png, .jpg, .jpeg"
                hidden
                @change="imageSelectCallback($event, doc._id)"
              />
            </div>
          </div>

          <!-- Post Button -->
          <div :id="doc._id + '_feedbtnflex'" class="d-flex justify-content-end flex-hidden">
            <button
              class="btn btn-primary clear-both"
              disabled
              :id="doc._id + '_feedbtn'"
              @click="submit(doc)"
            >Post</button>
          </div>
        </div>
        <div class="content-footer" v-else-if="doc.type === types[0] && canUserCreateContent">
          <div class="action-links d-flex justify-content-end"></div>

          <div
            style="border: none !important"
            class="action-links d-flex justify-content-end my-0 mt-2 py-0"
          >
            <!--button
                class="btn btn-sm btn-light mr-2 my-1"
                type="button"
                id="sendButton"
                aria-haspopup="true"
                aria-expanded="false">
                Edit
            </button-->
            <div class="dropdown">
              <button
                class="btn btn-sm btn-light mr-2 my-1 dropdown-toggle"
                type="button"
                id="sendButton"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <ion-icon name="ios-send"></ion-icon>Send
              </button>
              <div class="dropdown-menu" aria-labelledby="sendButton">
                <a
                  class="dropdown-item"
                  @click.prevent.stop="sendPostAsSMS(doc.body)"
                  v-if="doc.body.length <= MAX_CHAR_LEN"
                >SMS</a>
                <a
                  class="dropdown-item"
                  @click.prevent.stop="sendPostAsEmail({subject: doc.type === types[0] ? doc.topic.title : doc.title, body: doc.body})"
                >Email</a>
              </div>
            </div>
            <button
              @click="selectedPost = doc"
              data-toggle="modal"
              data-target="#addEditPostToMapModal"
              class="btn btn-sm btn-secondary my-1"
            >
              <ion-icon name="ios-pin"></ion-icon>
              <span>{{(doc.media_flags.find(f => f.type === 'location')) ? "Change Pin Location" : "Pin To Map"}}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <intersect @enter="reachedEndOfFeed()">
      <span></span>
    </intersect>
    <TopicThread :topic_id="selectedTopicID" @ThreadDismissed="threadDismissedCallback()" />
    <AddEditPostToMap
      :post="selectedPost"
      @AddEditPostToMap="addEditPostToMap"
      @RemovePostFromMap="removePostFromMap"
    />
  </div>
</template>

<script>
import DataHandlerService from "@/services/data";
import ContentService from "@/services/content";
import * as constants from "@/constants/index";
import FileUtils from "@/utils/file";
import RandomValues from "@/utils/random";
import { mapState, mapActions } from "vuex";
import TopicThread from "../TopicThread/TopicThread.vue";
import AddEditPostToMap from "../AddEditPostToMap/AddEditPostToMap.vue";
import Intersect from "vue-intersect";

const data = new DataHandlerService();
const contentService = new ContentService();
const fileUtils = new FileUtils();
const randomValues = new RandomValues();

export default {
  name: "FeedContent",
  components: { TopicThread, Intersect, AddEditPostToMap },
  props: ["feed", "source", "canUserCreateContent"],
  data() {
    return {
      types: ["media", "topic"],
      user: data.getUserData(),
      MAX_CHAR_LEN: constants.MAX_POST_CHAR_LEN,
      imagePool: [],
      selectedTopicID: null,
      selectedPost: null
    };
  },
  computed: {
    ...mapState(["circles"]),
    feedImages: function() {
      const images = [];
      const feedWithImages = this.$props.feed.filter(doc => doc.media_uploads);

      feedWithImages.forEach(doc => {
        doc.media_uploads.forEach(img => {
          images.push({ name: img.upload.path, id: img._id, doc_id: doc._id });
        });
      });

      return images;
    }
  },
  mounted() {
    this.$root.$on("ShowThread", id => {
      this.showThread(id);
    });
  },
  methods: {
    calculateColumns(photos) {
      if (photos.length === 1) return 1;
      else if (photos.length > 1 && photos.length < 5) return 2;
      else return 3;
    },
    checkValid(e, doc_id) {
      const submit_btn = document.getElementById(`${doc_id}_feedbtn`);
      const submit_btn_flex = document.getElementById(`${doc_id}_feedbtnflex`);
      const validation = document.getElementById(`${doc_id}_validation`);

      const valid = charLen => {
        validation.classList.add("text-light-grey");
        validation.classList.remove("form-error-text");
        validation.innerText = `${charLen}/${constants.MAX_POST_CHAR_LEN}`;
      };

      const invalid = charLen => {
        validation.classList.remove("text-light-grey");
        validation.classList.add("form-error-text");
        validation.innerText = `${charLen}/${constants.MAX_POST_CHAR_LEN}`;
      };

      if (!e) {
        submit_btn.disabled = true;
        submit_btn_flex.classList.add("flex-hidden");
        valid(0);
      } else {
        if (e.srcElement.value.length === 0) {
          submit_btn.disabled = true;
          submit_btn_flex.classList.add("flex-hidden");
          valid(0);
        } else {
          submit_btn_flex.classList.remove("flex-hidden");
          if (e.srcElement.value.length <= constants.MAX_POST_CHAR_LEN) {
            // inputEl.classList.remove(["touched", "invalid"]);
            submit_btn.disabled = false;
            valid(e.srcElement.value.length);
          } else {
            // inputEl.classList.add(["touched", "invalid"]);
            submit_btn.disabled = true;
            invalid(e.srcElement.value.length);
          }
        }
      }
    },
    submit(doc) {
      const inputEl = document.getElementById(`${doc._id}_feedinput`);
      const submit_btn = document.getElementById(`${doc._id}_feedbtn`);

      const form = {
        body: inputEl.value,
        status: "active",
        circle_id: doc.circle_id,
        topic_id: doc._id,
        profile_id: this.user._id
      };

      inputEl.disabled = true;
      submit_btn.disabled = true;
      submit_btn.innerText = "Posting";
      submit_btn.classList.add("btn--loading");

      const commentImages = this.getDocImages(doc._id);

      const successAction = function() {
        inputEl.disabled = false;
        inputEl.value = "";
        submit_btn.innerText = "Post";
        submit_btn.classList.remove("btn--loading");
        this.$notification.new("Comment posted");
        this.imagePool = this.imagePool.filter(s => s.doc_id !== doc._id);
      };

      const failureAction = function(response) {
        inputEl.disabled = false;
        submit_btn.disabled = false;
        submit_btn.innerText = "Post";
        submit_btn.classList.remove("btn--loading");
        this.$root.$emit("ShowErrorNotification", response.message);
      };

      if (commentImages.length > 0) {
        contentService
          .createCommentWithImages(form, commentImages)
          .then(post => {
            if (post) {
              this.$root.$emit("SaveNewPost", post);
            }
            successAction.call(this);
          })
          .catch(response => failureAction.call(this, response));
      } else {
        contentService
          .createComment(form)
          .then(post => {
            if (post) {
              this.$root.$emit("SaveNewPost", post);
            }
            successAction.call(this);
          })
          .catch(response => failureAction.call(this, response));
      }
    },
    getDocImages(doc_id) {
      return this.imagePool.filter(s => s.doc_id === doc_id);
    },
    removeImage(imageId) {
      this.imagePool.splice(
        this.imagePool.findIndex(s => s.id === imageId),
        1
      );
    },
    toggleUpload(doc_id) {
      const uploads_group = document.getElementById(`uploads-group${doc_id}`);
      uploads_group.hidden = !uploads_group.hidden;
    },
    selectImageFromFS(doc_id) {
      if (this.getDocImages(doc_id) !== 4) {
        const hiddenImageInput = document.getElementById(
          `${doc_id}_image-input`
        );
        hiddenImageInput.click();
      }
    },
    async imageSelectCallback(e, doc_id) {
      const files = e.target.files || e.dataTransfer.files;
      const validationStatus = fileUtils.validateFile(files);
      if (validationStatus.valid) {
        this.imagePool.push({
          id: randomValues.secureRandString(),
          file: files[0],
          src: await fileUtils.getSrc(files),
          doc_id
        });
      } else {
        this.$root.$emit("ShowErrorNotification", validationStatus.message);
      }
    },
    canUserComment(circle_id) {
      return this.circles.findIndex(s => s._id === circle_id) > -1;
    },
    getImagesForLightBox(doc_id) {
      return this.feedImages.filter(image => image.doc_id === doc_id);
    },
    showImage(name, doc_id) {
      if (Array.isArray(this.$refs.lightbox)) {
        const lightbox = this.$refs.lightbox.find(
          s => s.$el.id === `lightbox${doc_id}`
        );
        if (lightbox) {
          lightbox.show(name);
        }
      } else {
        this.$refs.lightbox.show(name);
      }
    },
    showThread(topic_id) {
      this.selectedTopicID = topic_id;
      this.$nextTick(function() {
        document.getElementById("toggleBtn").click();
      });
    },
    reachedEndOfFeed() {
      switch (this.$props.source) {
        case "main":
          this.$root.$emit("fetchMoreContent_Feed");
          break;

        case "circle":
          this.$root.$emit("fetchMoreContent_Circle");
          break;

        case "profile":
          this.$root.$emit("fetchMoreContent_Profile");
          break;

        default:
          this.$root.$emit("fetchMoreContent_Feed");
          break;
      }
    },
    threadDismissedCallback() {
      this.selectedTopicID = null;
    },
    addEditPostToMap(flag) {
      if (flag._id) {
        contentService
          .editMediaFlag(flag)
          .then(() => {
            this.$root.$emit("SaveModifiedPost", flag);
            this.$notification.new("Location modified");
          })
          .catch(err => this.$root.$emit("ShowErrorNotification", err.message));
      } else {
        contentService
          .addMediaFlag(flag)
          .then(_id => {
            flag._id = _id;
            this.$root.$emit("SaveModifiedPost", flag);
            this.$notification.new("Post added to map");
          })
          .catch(err => this.$root.$emit("ShowErrorNotification", err.message));
      }
    },
    removePostFromMap(flag) {
      contentService
        .deleteMediaFlag(flag._id)
        .then(() => {
          this.$root.$emit("RemoveFlagFromPost", flag);
          this.$notification.new("Post removed from map");
        })
        .catch(err => this.$root.$emit("ShowErrorNotification", err.message));
    },
    sendPostAsSMS(post) {
      this.$root.$emit("sendPostAsSMS", post);
    },
    sendPostAsEmail(post) {
      this.$root.$emit("sendPostAsEmail", post);
    },
    openTopicEditor(topic) {
      this.$root.$emit("openTopicEditor", topic);
    }
  }
};
</script>

<style lang="scss" scoped src="./FeedContent.scss"></style>